import type { Vector3 } from 'three';
import { AbilityTracker } from '../../characters/AbilityTracker';
import type { AbilityContext, AbilityDef } from '../../characters/abilities';
import type { AbilityWorld } from '../../game/AbilityWorld';
import { EYE_HEIGHT, PLAYER_HEIGHT } from '../../movement/constants';
import type { PlayerController } from '../../movement/PlayerController';
import type { CombatWorld } from '../CombatWorld';

/** Ranges/cadence for the seek-orbit-cast brain (feature params, spec 0028). */
export const DUELIST_ENGAGE_RANGE = 900; // beyond this: run straight at the target
export const DUELIST_MIN_RANGE = 160; // inside this: stop pushing forward
export const DUELIST_TURN_PER_TICK = (4 * Math.PI) / 180; // rad — beatable by a surfer
export const DUELIST_ORBIT_FLIP_TICKS = 180; // strafe direction flips every 1.8 s

export interface DuelistOptions {
  /** Combat id this brain fights as (its `selfId` for casts). */
  id: string;
  controller: PlayerController;
  combat: CombatWorld;
  /** Abilities it may cast (uses each def's cooldown; ticked at sim dt). */
  abilities: readonly AbilityDef[];
  /** Cast range per ability id — the brain casts when the target is inside. */
  castRange: Record<string, number>;
  world: AbilityWorld;
  /** Live target feet position (usually the player's). */
  targetPos: () => Vector3;
}

/**
 * The first AI champion brain (spec 0028): a deterministic seek/orbit/cast
 * policy driving a real PlayerController — no second physics path (ADR 0007),
 * no randomness (orbit flips on a fixed tick cadence), headless-testable.
 */
export class Duelist {
  private yaw = 0;
  private tickCount = 0;
  private readonly cooldowns = new AbilityTracker();

  constructor(private readonly opts: DuelistOptions) {}

  /** Advance one fixed tick (skips itself while dead — the host gates on alive). */
  tick(dt: number): void {
    const { controller, combat, id } = this.opts;
    this.tickCount++;
    this.cooldowns.tick(dt);

    const target = this.opts.targetPos();
    const dx = target.x - controller.position.x;
    const dz = target.z - controller.position.z;
    const dist = Math.hypot(dx, dz);

    // Rate-limited turn toward the target (yaw 0 faces -Z).
    const desiredYaw = Math.atan2(-dx, -dz);
    const diff = wrapAngle(desiredYaw - this.yaw);
    this.yaw +=
      Math.abs(diff) <= DUELIST_TURN_PER_TICK ? diff : Math.sign(diff) * DUELIST_TURN_PER_TICK;

    // Seek when far; orbit (strafe, deterministic flip) when engaged.
    const engaged = dist < DUELIST_ENGAGE_RANGE;
    const orbit = Math.floor(this.tickCount / DUELIST_ORBIT_FLIP_TICKS) % 2 === 0 ? 1 : -1;
    controller.tick(
      {
        forward: dist > DUELIST_MIN_RANGE ? 1 : 0,
        right: engaged ? orbit : 0,
        jump: false,
      },
      this.yaw,
      dt,
    );

    // Cast anything ready and in range, facing the target. Projectiles leave the
    // caster's eye, so aim from there at the target's capsule mid — negative
    // pitch looks down (MouseLook convention, see characters/aim.test.ts).
    const eyeY = controller.position.y + EYE_HEIGHT;
    const aimY = target.y + PLAYER_HEIGHT / 2;
    const pitch = Math.atan2(aimY - eyeY, Math.max(1e-6, dist));
    for (const ability of this.opts.abilities) {
      const range = this.opts.castRange[ability.id];
      if (range === undefined || dist > range) continue;
      if (!this.cooldowns.ready(ability.id)) continue;
      const ctx: AbilityContext = {
        player: controller,
        yaw: this.yaw,
        pitch,
        world: this.opts.world,
        combat,
        selfId: id,
      };
      if (ability.activate?.(ctx) !== false) {
        this.cooldowns.trigger(ability.id, ability.cooldown);
      }
    }
  }

  currentYaw(): number {
    return this.yaw;
  }
}

function wrapAngle(a: number): number {
  const tau = 2 * Math.PI;
  return ((((a + Math.PI) % tau) + tau) % tau) - Math.PI;
}
